import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { useUpdateWorkspaceMembersMutation } from "@dashboard/workspaces/api";
import { useWorkspaceMemberRole } from "@dashboard/workspaces/api/helpers";
import { WorkspaceMember } from "@dashboard/workspaces/types";
import { DialogContentText } from "@mui/material";
import AuthService from "@services/authService";
import { PermissionService } from "@services/permissionService/permissionService";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { useCurrentWorkspace } from "../../../api/helpers";

type Props = {
    member: WorkspaceMember;
};
export const RemoveWorkspaceMemberButton = (props: Props) => {
    const [dialogOpen, setDialogOpen] = React.useReducer((state) => !state, false);
    const [loading, setLoading] = React.useState(false);
    const [updateWorkspaceMembers] = useUpdateWorkspaceMembersMutation();

    const { t } = useTranslation(["common", "pages", "modals"]);

    const ownEmail = AuthService.getCurrentUser()?.email;

    const { data: workspace, isLoading: isWorkspaceLoading, isError: isWorkspaceError } = useCurrentWorkspace();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { workspaceAbility } = PermissionService();
    const mayManageMember = workspaceAbility(workspace).can("manage", "Member");

    const currentLoggedUserRole = useWorkspaceMemberRole();
    const isMemberOwner = props.member.role === "Owner";
    const isWorkspaceMember = workspace?.members.some((m) => m.email === props.member.email);

    const onConfirmDelete = async () => {
        if (!workspaceId) {
            return;
        }

        setLoading(true);
        await updateWorkspaceMembers({
            id: workspaceId,
            removedMembers: [props.member.email],
        })
            .unwrap()
            .then(() => showSuccessToast(t("pages:workspacePage.toast.membersUpdateSuccess")))
            .finally(() => setLoading(false));
    };

    const getButtonProps = () => {
        if (!isWorkspaceMember) {
            return { title: t("pages:workspacePage.tooltip.memberIsNotWorkspaceMember"), mayRemove: false };
        }
        if (isMemberOwner && currentLoggedUserRole === "Manager") {
            return { title: t("pages:workspacePage.tooltip.managersCannotDeleteOwners"), mayRemove: false };
        }
        if (props.member.role === "User" && props.member.email === ownEmail) {
            return { title: t("pages:workspacePage.tooltip.deleteMember"), mayRemove: true };
        }
        if (!mayManageMember) {
            return { title: t("pages:workspacePage.tooltip.cannotManageMembers"), mayRemove: false };
        }
        return { title: null, mayRemove: true };
    };

    const { title, mayRemove } = getButtonProps();

    if (isWorkspaceLoading || isWorkspaceError) {
        return null;
    }

    return (
        <React.Fragment>
            <DangerOutlinedButton
                onClick={setDialogOpen}
                disabled={!mayRemove}
                tooltipProps={{ title, hide: !title }}
                loading={loading}
            >
                <i className="fas fa-trash-alt" />
            </DangerOutlinedButton>

            <ConfirmDialog
                title={t("modals:removeMember.title")}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={dialogOpen}
                onConfirm={onConfirmDelete}
                onClose={setDialogOpen}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="removeMember.description"
                        values={props.member}
                        components={{
                            bold: <span style={{ fontWeight: "bold" }} />,
                        }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};
